import axios from 'axios'
import { getAuthToken } from './authService'
import { task } from '@/config/api'

const taskClient = axios.create({
  baseURL: task,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true
})

taskClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

taskClient.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    // Handle authentication errors (including IP mismatch)
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      // Clear authentication data
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user_data')
      document.cookie = 'session_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'

      // Redirect to login if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

export async function createTask(taskData) {
  try {
    const response = await taskClient.post('/tasks', taskData)
    return response.data
  } catch (error) {
    console.error('Error creating task:', error)
    throw error
  }
}

export async function getTasks(limit = 50, offset = 0) {
  try {
    const response = await taskClient.get('/tasks', {
      params: { limit, offset }
    })
    return response.data
  } catch (error) {
    console.error('Error getting tasks:', error)
    throw error
  }
}

export async function getTask(taskId) {
  try {
    const response = await taskClient.get(`/tasks/${taskId}`)
    return response.data
  } catch (error) {
    console.error('Error getting task:', error)
    throw error
  }
}

export async function updateTask(taskId, updateData) {
  try {
    const response = await taskClient.put(`/tasks/${taskId}`, updateData)
    return response.data
  } catch (error) {
    console.error('Error updating task:', error)
    throw error
  }
}

export async function deleteTask(taskId) {
  try {
    const response = await taskClient.delete(`/tasks/${taskId}`)
    return response.data
  } catch (error) {
    console.error('Error deleting task:', error)
    throw error
  }
}



export function validateTaskData(taskData) {
  const errors = []

  if (!taskData.task || taskData.task.trim().length === 0) {
    errors.push('Task description is required')
  }

  if (taskData.task && taskData.task.length > 2000) {
    errors.push('Task description must be less than 2000 characters')
  }

  if (taskData.phone_number && taskData.phone_number.length > 20) {
    errors.push('Phone number must be less than 20 characters')
  }

  if (taskData.website_links && !Array.isArray(taskData.website_links)) {
    errors.push('Website links must be an array')
  }

  if (taskData.website_links && taskData.website_links.length > 10) {
    errors.push('Maximum 10 website links allowed')
  }

  if (taskData.website_links) {
    for (const link of taskData.website_links) {
      if (typeof link !== 'string' || !isValidUrl(link)) {
        errors.push('All website links must be valid URLs')
        break
      }
    }
  }

  if (taskData.schedule_time) {
    const scheduleDate = new Date(taskData.schedule_time)
    const now = new Date()
    if (scheduleDate <= now) {
      errors.push('Schedule time must be in the future')
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}

function isValidUrl(string) {
  try {
    const url = new URL(string)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch (_) {
    return false
  }
}
